/**
 * One action's busy flag and failure, written once.
 *
 * A settings action that is not a value — disconnecting a rental system,
 * removing a person, inviting one — runs the same machine as a save: busy
 * while it runs, the last failure cleared on the next try, the failure
 * worded by `errorMessage` for the modal or row that fired it (DESIGN.md
 * "Error placement"). It was written out as try/catch/finally eight times
 * across the settings sections; `useSettingCommit` runs its saves through
 * this as well, so a save and an action fail the same way.
 *
 * `run(fn)` resolves true when `fn` finished and false when it threw; it
 * never rejects, so a caller does what follows success inside `fn` and
 * nothing at all on failure.
 */
import { useState } from "react";
import { errorMessage } from "./error-message";

export interface Action {
  working: boolean;
  error: string | null;
  setError: (next: string | null) => void;
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
}

export function useAction(): Action {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setWorking(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setWorking(false);
    }
  };

  return { working, error, setError, run };
}

/** Enter leaves a one-line field, which is what commits it. */
export function blurOnEnter(e: React.KeyboardEvent<HTMLInputElement>) {
  if (e.key === "Enter") e.currentTarget.blur();
}
